import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { VacancyDetail } from '@/types/recruitment';
import { Columns3, X } from 'lucide-react';
import { VacancyPipelineTab } from './vacancy-pipeline-tab';

interface VacancySettingsDialogProps {
  vacancy: VacancyDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function VacancySettingsDialog({
  vacancy,
  open,
  onOpenChange,
}: VacancySettingsDialogProps) {
  const stagesCount = vacancy.pipelineStages.length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex max-h-[85vh] w-[680px] max-w-[680px] flex-col gap-0 overflow-hidden rounded-xl p-0 [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Board Settings</DialogTitle>
        <DialogDescription className="sr-only">
          Configure the pipeline for {vacancy.title}
        </DialogDescription>

        <div className="flex shrink-0 items-center justify-between border-b border-border px-6 py-[18px]">
          <div className="flex items-center gap-2.5">
            <svg
              className="h-[18px] w-[18px] shrink-0 text-muted-foreground"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="text-base font-bold tracking-tight">Board Settings</span>
          </div>
          <button
            type="button"
            className="flex h-[30px] w-[30px] cursor-pointer items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:bg-muted/80 hover:text-foreground"
            onClick={() => onOpenChange(false)}
            aria-label="Close"
          >
            <X className="h-[15px] w-[15px]" />
          </button>
        </div>

        <Tabs defaultValue="pipeline" className="flex min-h-0 flex-1 flex-col">
          <TabsList
            data-slot="vacancy-settings-tabs"
            className="h-auto w-full shrink-0 justify-start gap-0 rounded-none border-b border-border bg-muted/40 p-0 px-6 [&>button]:bg-transparent [&>button[data-state=active]]:bg-transparent [&>button[data-state=active]]:shadow-none"
          >
            <TabsTrigger
              value="pipeline"
              className="gap-1.5 rounded-none border-b-2 border-transparent px-4 py-3 text-[13px] font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground data-[state=active]:border-b-2 data-[state=active]:border-hygge data-[state=active]:font-semibold data-[state=active]:text-foreground"
            >
              <Columns3 className="h-3.5 w-3.5" />
              Pipeline
              <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-muted-foreground">
                {stagesCount}
              </span>
            </TabsTrigger>
          </TabsList>

          <div className="flex-1 overflow-y-auto">
            <TabsContent
              value="pipeline"
              className="mt-0 p-5 duration-150 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=inactive]:animate-out data-[state=inactive]:fade-out-0"
            >
              <VacancyPipelineTab vacancy={vacancy} />
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
